<template>
  <div id="judging-criteria-list" class="page-container">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
    ></overlay-loading-progress>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8">
              <div class="d-flex align-center">
                <div class="header-icon-box">
                  <v-icon size="36" color="white">mdi-clipboard-check</v-icon>
                </div>
                <div class="ml-6">
                  <h1 class="header-title white--text">{{ PageTitle }}</h1>
                  <p class="header-subtitle white--text">
                    {{ PageDescription }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <v-btn
                @click.prevent="addRecordPrompt = true"
                color="white"
                elevation="6"
                rounded
                height="44"
                class="action-btn mr-2"
              >
                <v-icon small class="mr-2">mdi-plus-circle</v-icon>
                CREATE
              </v-btn>
              <v-btn
                @click.prevent="refreshPageData"
                color="white"
                elevation="6"
                rounded
                height="44"
                class="action-btn"
              >
                <v-icon small class="mr-2">mdi-refresh</v-icon>
                REFRESH
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Search Section -->
    <v-card elevation="8" class="search-card mb-6">
      <v-card-text class="pa-6">
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="searchForm"
        >
          <v-row wrap align="center">
            <v-col cols="12" sm="6" lg="4" md="6">
              <div class="form-field-wrapper">
                <label class="form-field-label">
                  <v-icon small class="mr-2" color="primary">mdi-trophy</v-icon>
                  <span><span class="text-danger">*</span> Award Type</span>
                </label>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="AwardTypeOptions"
                  :rules="AwardTypeRules"
                  v-model="AwardType"
                  dense
                  clearable
                  required
                  outlined
                  class="form-control-modern"
                ></v-autocomplete>
              </div>
            </v-col>
            <v-col cols="12" sm="6" lg="4" md="6">
              <v-btn
                :disabled="!valid1"
                @click.prevent="searchForm"
                :loading="SubmitFlag"
                color="#8950fc"
                elevation="4"
                rounded
                height="48"
                class="search-btn white--text mt-4"
              >
                <v-icon small class="mr-2">mdi-magnify</v-icon>
                SEARCH
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Loading State -->
    <div v-if="LoadingFlag" class="text-center py-12">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <p class="mt-4 text-h6 primary--text">Loading criteria...</p>
    </div>

    <!-- Stats Alert -->
    <v-alert
      type="info"
      prominent
      border="left"
      class="stats-alert mb-6"
      v-if="tableData1.length > 0"
    >
      <template v-slot:prepend>
        <v-icon>mdi-list-box</v-icon>
      </template>
      <div class="alert-content">
        <p class="alert-title">Total Criteria Available</p>
        <h2 class="alert-number">{{ tableData1.length }}</h2>
        <p class="alert-subtitle">Ready to manage</p>
      </div>
    </v-alert>

    <!-- Data Table Section -->
    <v-card elevation="12" class="table-card" v-if="tableData1.length > 0">
      <!-- Search Bar -->
      <v-card-text class="search-bar-section pa-4">
        <v-text-field
          v-model="search"
          placeholder="Search criteria..."
          prepend-icon="mdi-magnify"
          outlined
          dense
          rounded
          class="search-field"
          style="max-width: 400px"
        ></v-text-field>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        class="modern-table"
        v-model="selected1"
        :search="search"
        :item-key="tableOptions1.ItemKey"
        :single-select="tableOptions1.SingleSelectFlag"
        :show-select="tableOptions1.ShowSelectFlag"
        :headers="tableColumns1"
        :items="tableData1"
        :items-per-page="tableOptions1.ItemsPerPage"
        :footer-props="tableOptions1.FooterProps"
        hover
        hide-default-footer
      >
        <!-- Status Chip Template -->
        <template v-slot:item.ActiveStatusTxt="{ item }">
          <v-chip
            :color="getActiveStatusColor(item.ActiveStatusTxt)"
            dark
            class="status-chip"
          >
            <v-icon small left>mdi-circle-medium</v-icon>
            {{ item.ActiveStatusTxt }}
          </v-chip>
        </template>

        <!-- Image Template -->
        <template v-slot:item.MemberImagePath="{ item }">
          <v-avatar size="60" class="my-2">
            <img :src="item.MemberImagePath" />
          </v-avatar>
        </template>

        <!-- Actions Template -->
        <template v-slot:item.Actions="{ item }">
          <div class="action-buttons-group">
            <v-btn
              v-if="item.PreviewFlag"
              icon
              small
              color="info"
              @click="previewAlert(item)"
              class="action-icon-btn"
              v-tooltip.top="'Preview'"
            >
              <v-icon size="18">mdi-file-search</v-icon>
            </v-btn>

            <v-btn
              v-if="item.QuestionsButtonFlag"
              icon
              small
              color="success"
              @click="addCriteriaQuestions(item)"
              class="action-icon-btn"
              v-tooltip.top="'Questions'"
            >
              <v-icon size="18">mdi-note-plus</v-icon>
            </v-btn>

            <v-btn
              v-if="item.EditButtonFlag"
              icon
              small
              color="warning"
              @click="editAlert(item)"
              class="action-icon-btn"
              v-tooltip.top="'Edit'"
            >
              <v-icon size="18">mdi-pencil</v-icon>
            </v-btn>

            <v-btn
              v-if="item.DeleteButtonFlag"
              icon
              small
              color="error"
              @click="deleteAlert(item)"
              class="action-icon-btn"
              v-tooltip.top="'Delete'"
            >
              <v-icon size="18">mdi-trash-can</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Custom Footer -->
      <v-divider></v-divider>
      <v-card-text class="table-footer pa-4">
        <div class="d-flex align-center justify-space-between">
          <span class="text-caption text-grey-1">
            Showing <strong>{{ tableData1.length }}</strong> criteria
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Empty State -->
    <v-card
      elevation="8"
      class="empty-state-card"
      v-if="tableData1.length === 0 && !LoadingFlag"
    >
      <v-card-text class="text-center pa-12">
        <v-icon size="100" color="#ccc" class="mb-4">mdi-folder-open</v-icon>
        <h3 class="text-h6 text-grey-1 mb-2">No Criteria Found</h3>
        <p class="text-grey-4 mb-4">
          Select an award type and search to view judging criteria
        </p>
      </v-card-text>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="75%">
      <judging-criteria-create
        @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        :recordData="recordData"
        v-if="addRecordPrompt"
      ></judging-criteria-create>
    </v-dialog>

    <v-dialog v-model="editRecordPrompt" persistent max-width="75%">
      <judging-criteria-edit
        :editRecordPrompt="editRecordPrompt"
        @hideEditRecordPrompt="hideEditRecordPrompt($event)"
        :CriteriaId="recordData.JudgingCriteriaId"
        v-if="editRecordPrompt"
      ></judging-criteria-edit>
    </v-dialog>

    <v-dialog v-model="questionsRecordPrompt" persistent max-width="85%">
      <judging-criteria-questions-list
        @hideQuestionsRecordPrompt="hideQuestionsRecordPrompt($event)"
        :CriteriaId="JudgingCriteriaId"
        v-if="questionsRecordPrompt"
      ></judging-criteria-questions-list>
    </v-dialog>
  </div>
</template>
    
  <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import JudgingCriteriaCreate from "@/view/pages/erp/jci-award/judging-criteria/JudgingCriteriaCreate.vue";
import JudgingCriteriaEdit from "@/view/pages/erp/jci-award/judging-criteria/JudgingCriteriaEdit.vue";
import JudgingCriteriaQuestionsList from "@/view/pages/erp/jci-award/judging-criteria-questions/JudgingCriteriaQuestionsList.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    JudgingCriteriaCreate,
    JudgingCriteriaEdit,
    JudgingCriteriaQuestionsList,
  },
  data() {
    return {
      valid1: true,
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,

      SelectFlag: true,
      DeleteFlag: false,
      SubmitFlag: false,

      alert: {},

      valid: true,

      RoleId: "",

      ResultFlag: 0,
      OverlayLoadingFlag: false,
      AwardTypeRules: [(v) => !!v || "Award Type is required"],
      AwardType: {},
      AwardTypeOptions: [],
      isInitializing: true,

      addRecordPrompt: false,
      editRecordPrompt: false,
      questionsRecordPrompt: false,
      previewRecordPrompt: false,

      recordData: {},

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: [],
      tableColumns1: [],
      TotalMembers: 0,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
      selectedData: {},
      JudgingCriteriaId: 0,
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.getAwardTypeOptions();
      }
    },
    AwardType: function () {
      console.log("watch AwardType");
      // Only reset if not initializing - don't clear data on selection change
      if (!this.isInitializing) {
        var temp_code = this.AwardType;
        console.log("temp_code=" + temp_code);
        var idx = this.AwardTypeOptions.map((e) => e.value).indexOf(temp_code);
        console.log("idx=" + idx);
        if (idx >= 0) {
          var tr = this.AwardTypeOptions[idx];
          console.log({ tr });
          this.recordData = tr;
        }
      }
    },
    AwardTypeOptions: function () {
      console.log("watch AwardTypeOptions");
      this.AwardTypeOptionsLoading = false;
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewAlert(tr) {
      console.log("previewAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.recordData = tr;
        this.previewRecordPrompt = true;
      } else {
        var message = "Kindly select one record to preview";
        this.errorMessage(message);
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    hideQuestionsRecordPrompt(flag) {
      console.log("hideQuestionsRecordPrompt called");
      this.questionsRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    editAlert(tr) {
      console.log("editAlert called");
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.recordData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    addCriteriaQuestions(tr) {
      console.log("addCriteriaQuestions called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.JudgingCriteriaId = tr.JudgingCriteriaId;
        console.log({ JudgingCriteriaId: this.JudgingCriteriaId });
        this.questionsRecordPrompt = true;
      } else {
        var message = "Kindly select one criteria to view questions";
        this.toast("error", message);
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "judging_criteria",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getAwardTypeOptions() {
      console.log("getAwardTypeOptions called");
      var selectbox1_source = "AwardType";
      var selectbox1_destination = "AwardTypeOptions";
      var selectbox1_url = "api/award-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
      // Mark initialization complete after getting options
      setTimeout(() => {
        this.isInitializing = false;
      }, 100);
    },
    searchForm() {
      console.log("searchForm called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.tableData1 = [];
        this.selected1 = [];
        this.SubmitFlag = true;
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judging-criteria/lists";
        var upload = {
          UserInterface: 1,
          AwardType: this.AwardType,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var table_content = [];
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        // thisIns.ProgessStart = 1;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.ProgessStart = 0;
            thisIns.SubmitFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            var options = records.TableOptions;

            thisIns.tableOptions1 = options;
            thisIns.LoadingFlag = false;
            thisIns.SubmitFlag = false;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;

              thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.SubmitFlag = false;
          });
      } else {
        var message = "Kindy fill required fields and then submit";
        this.toast("error", message);
      }
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      console.log("tr=" + JSON.stringify(tr));
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.confirmAlert();
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    confirmAlert() {
      console.log("confirmAlert called");
      var tr = this.selectedData;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/>Award Type <b> " + tr.AwardTypeTitle + "</b>";
        htmlTxt +=
          "<br/>Criteria Detail <b> " + tr.JudgingCriteriaDetail + "</b>";
        htmlTxt +=
          "<br/>Criteria Score <b> " + tr.JudgingCriteriaScore + "</b>";
        htmlTxt += "<br/><br/>Do you want to delete the data?";

        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete the data?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    deleteRecord(tr) {
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judging-criteria/destroy";
        var upload = {
          UserInterface: 1,
          CriteriaId: tr.JudgingCriteriaId,
          CriteriaDetail: tr.JudgingCriteriaDetail,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
    addRecordPrompt(tr) {
      console.log(" addRecordPrompt called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.selectedData = tr;
      } else {
        var message = "Kindly select one Award to create";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
// Color Palette
$primary-color: #8950fc;
$secondary-color: #b385e7;
$gradient-main: linear-gradient(
  135deg,
  $primary-color 0%,
  $secondary-color 100%
);
$success-color: #10b981;
$danger-color: #f44336;
$card-shadow: 0 16px 48px rgba(137, 80, 252, 0.15);

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shiny {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

#judging-criteria-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;
  animation: fadeIn 0.6s ease-in-out;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: $gradient-main !important;
    border-radius: 16px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(137, 80, 252, 0.4);

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 1;
    }
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
  }

  .header-title {
    font-size: 32px;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.8s ease-out;
    letter-spacing: 0.5px;
    margin: 0;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.95;
    animation: fadeInUp 1s ease-out;
    letter-spacing: 0.3px;
    margin: 0;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: $primary-color !important;
    background: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
    }

    .v-icon {
      font-size: 16px !important;
    }
  }

  // ============================================
  // SEARCH CARD
  // ============================================
  .search-card {
    border-radius: 12px !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s backwards;
    background: white !important;
    box-shadow: 0 12px 40px rgba(137, 80, 252, 0.15) !important;
  }

  .form-field-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-field-label {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    color: #333;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    margin: 0;

    .v-icon {
      color: $primary-color;
    }

    .text-danger {
      color: #e74c3c;
      font-weight: 800;
    }
  }

  .form-control-modern {
    ::v-deep {
      .v-input__control {
        .v-input__slot {
          background: white !important;
          border-radius: 8px !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(137, 80, 252, 0.2) !important;

          &:hover {
            border-color: rgba(137, 80, 252, 0.4) !important;
          }

          &:focus,
          &:active {
            border-color: $primary-color !important;
            box-shadow: 0 0 0 3px rgba(137, 80, 252, 0.1);
          }
        }
      }
    }
  }

  .search-btn {
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 4px 16px rgba(137, 80, 252, 0.25) !important;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(137, 80, 252, 0.4) !important;
    }

    &:disabled {
      opacity: 0.5;
    }
  }

  // ============================================
  // STATS ALERT (ENHANCED)
  // ============================================
  .stats-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(137, 80, 252, 0.12) 0%,
      rgba(29, 185, 84, 0.08) 100%
    ) !important;
    border-left: 5px solid $primary-color !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(137, 80, 252, 0.15) !important;
    padding: 16px 20px !important;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.08) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }

    ::v-deep {
      .v-alert__icon {
        color: $primary-color !important;
        align-self: flex-start !important;
        margin-top: 0px !important;
        font-size: 22px !important;
      }

      .v-alert__content {
        position: relative;
        z-index: 1;
      }
    }
  }

  .alert-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .alert-title {
    font-size: 11px;
    font-weight: 700;
    color: $primary-color;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
  }

  .alert-number {
    font-size: 32px;
    font-weight: 800;
    color: $primary-color;
    margin: 4px 0 2px 0;
    line-height: 1;
  }

  .alert-subtitle {
    font-size: 11px;
    color: $secondary-color;
    margin: 0;
    opacity: 0.9;
    font-weight: 500;
    letter-spacing: 0.2px;
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 12px !important;
    overflow: hidden;
    animation: fadeInUp 0.8s ease-out 0.2s backwards;
    background: white !important;
    box-shadow: $card-shadow !important;

    .search-bar-section {
      background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
      border-bottom: 1px solid rgba(137, 80, 252, 0.1);

      .search-field {
        ::v-deep .v-input__prepend-inner {
          margin-right: 12px;
        }

        ::v-deep .v-input__control {
          input::placeholder {
            color: #bdc3c7 !important;
            opacity: 1;
          }
        }
      }
    }

    .modern-table {
      ::v-deep {
        .v-data-table__wrapper {
          table {
            border-collapse: collapse;

            thead {
              tr {
                background: linear-gradient(
                  90deg,
                  $primary-color 0%,
                  $secondary-color 100%
                );

                th {
                  background: transparent !important;
                  color: white !important;
                  font-weight: 700 !important;
                  font-size: 13px !important;
                  padding: 16px 12px !important;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  border: none !important;
                }
              }
            }

            tbody {
              tr {
                border-bottom: 1px solid rgba(137, 80, 252, 0.1) !important;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

                &:hover {
                  background: linear-gradient(
                    90deg,
                    rgba(137, 80, 252, 0.05) 0%,
                    rgba(29, 185, 84, 0.05) 100%
                  ) !important;
                  transform: scale(1.005);
                  box-shadow: inset 0 2px 8px rgba(137, 80, 252, 0.05);
                }

                td {
                  padding: 14px 12px !important;
                  color: #333;
                  font-size: 13px;
                }

                &:last-child {
                  border-bottom: none !important;
                }
              }
            }
          }
        }
      }
    }

    .table-footer {
      background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
      border-top: 1px solid rgba(137, 80, 252, 0.1);
      padding: 12px 24px !important;
    }
  }

  // ============================================
  // STATUS CHIP
  // ============================================
  .status-chip {
    font-weight: 700 !important;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    font-size: 11px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
    }
  }

  // ============================================
  // ACTION BUTTONS
  // ============================================
  .action-buttons-group {
    display: flex;
    gap: 6px;
    justify-content: center;
  }

  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 6px !important;

    &:hover {
      transform: scale(1.25) rotate(5deg);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: scale(0.9);
    }
  }

  // ============================================
  // EMPTY STATE
  // ============================================
  .empty-state-card {
    border-radius: 12px !important;
    border: 2px dashed rgba(137, 80, 252, 0.2) !important;
    background: linear-gradient(
      135deg,
      rgba(137, 80, 252, 0.02) 0%,
      rgba(29, 185, 84, 0.02) 100%
    ) !important;
    animation: fadeInUp 0.6s ease-out;
    box-shadow: $card-shadow !important;

    .v-card__text {
      padding: 40px 20px !important;

      .v-icon {
        animation: float 4s ease-in-out infinite;
      }

      h3 {
        color: $primary-color;
        font-weight: 700;
        margin-bottom: 8px;
      }

      p {
        color: #999;
        font-size: 14px;
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 1200px) {
    .header-title {
      font-size: 26px;
    }

    .header-subtitle {
      font-size: 14px;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 10px;

    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 12px;
    }

    .header-icon-box {
      width: 48px;
      height: 48px;
      padding: 10px;
    }

    .action-btn {
      font-size: 12px !important;
      padding: 8px 12px !important;

      .v-icon {
        font-size: 14px !important;
      }
    }

    .alert-number {
      font-size: 24px;
    }
  }

  @media (max-width: 600px) {
    padding: 15px 8px;

    .header-card {
      margin-bottom: 12px;

      ::v-deep .v-card__text {
        padding: 12px !important;
      }
    }

    .header-title {
      font-size: 18px;
    }

    .header-subtitle {
      font-size: 11px;
    }

    .header-icon-box {
      width: 40px;
      height: 40px;
      padding: 8px;

      ::v-deep .v-icon {
        font-size: 24px !important;
      }
    }

    .action-btn {
      font-size: 10px !important;
      padding: 6px 10px !important;
      margin-bottom: 8px !important;

      .v-icon {
        font-size: 12px !important;
      }
    }

    .form-field-label {
      font-size: 12px;
    }

    .search-btn {
      width: 100%;
    }

    .alert-number {
      font-size: 20px;
    }

    .action-buttons-group {
      gap: 4px;
    }

    .action-icon-btn {
      ::v-deep .v-icon {
        font-size: 16px !important;
      }
    }
  }

  // ============================================
  // UTILITY CLASSES
  // ============================================
  .page-container {
    width: 100%;
  }

  .text-right {
    text-align: right;
  }

  .ml-6 {
    margin-left: 24px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .mb-4 {
    margin-bottom: 16px;
  }

  .mb-2 {
    margin-bottom: 8px;
  }

  .mr-2 {
    margin-right: 8px;
  }

  .py-12 {
    padding-top: 48px;
    padding-bottom: 48px;
  }

  .mt-4 {
    margin-top: 16px;
  }

  .pa-8 {
    padding: 32px;
  }

  .pa-6 {
    padding: 24px;
  }

  .pa-4 {
    padding: 16px;
  }

  .pa-12 {
    padding: 48px;
  }

  .d-flex {
    display: flex;
  }

  .align-center {
    align-items: center;
  }

  .justify-space-between {
    justify-content: space-between;
  }

  .white--text {
    color: white;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .text-danger {
    color: #e74c3c !important;
    font-weight: 800;
  }

  .text-grey-1 {
    color: #999 !important;
  }

  .text-grey-4 {
    color: #bbb !important;
  }

  .text-h6 {
    font-size: 20px;
  }

  .text-caption {
    font-size: 12px;
  }

  .primary--text {
    color: $primary-color;
  }
}
</style>